import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  Check,
  ChevronsRight,
  Copy,
  Crown,
  LogOut,
  Pause,
  Play,
  Plus,
  Send,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  partyMediaLabel,
  partyPlayableText,
  type PartyChatMessage,
  type PartyMember,
  type PartyState,
} from '@teaplus/shared';
import { detailsPath } from '../media/media-card';
import { ProfileAvatar } from '../profile/avatar';
import { Button } from '../ui/button';
import {
  Chat,
  MemberMenu,
  partyActivityText,
  PartyMenu,
  PartyQueue,
  Reactions,
  ReadyCheckCard,
  statusOf,
} from './party-panel';
import { api, errorMessage } from '@/lib/api';
import {
  copyText,
  forgetParty,
  leaveParty,
  partyWatchPath,
  setActiveParty,
  setPartyDockOpen,
  startParty,
  useActiveParty,
  useLinks,
  useMembersToasts,
  usePartyDock,
  usePartyEnabled,
  type PartyConnection,
  type PartyTitle,
} from '@/lib/party';
import { placeText, useLookingTitle } from '@/lib/presence';
import { useActiveProfile } from '@/lib/queries';
import { useCanSocialise, useFriends } from '@/lib/social';
import { cn } from '@/lib/cn';

const fail = (e: unknown) => toast.error(errorMessage(e));
const notify = (t: string) => toast(t);

/** A title is on, whether or not you're watching it. */
export const partyIsLive = (state: PartyState) =>
  state.hasTitle &&
  (state.status === 'starting' || state.status === 'playing' || state.status === 'paused');

/** Is the film on while you're in the menus? Then you can jump in. */
export const filmOnWithoutYou = (state: PartyState, me: PartyMember | null) =>
  partyIsLive(state) && !!me?.sittingOut;

export function jumpIn(conn: PartyConnection, state: PartyState, go: (to: string) => void) {
  void conn.act({ type: 'sitOut', out: false }).catch(fail);
  go(partyWatchPath(state.media, state.code));
}

/**
 * Keeps this window in its party while you browse: it stays connected, says what you're looking
 * at, and opens the player for everyone when a title starts. Draws nothing.
 */
export function PartyFollower() {
  const { conn, state, error, me } = useActiveParty();
  const code = conn?.code ?? null;
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const looking = useLookingTitle();
  const profileId = useActiveProfile().data?.profile.id;
  useMembersToasts(state, notify);

  // A party belongs to a profile: switching profile takes this window out of it.
  const profileRef = useRef(profileId);
  useEffect(() => {
    if (profileRef.current && profileId && profileRef.current !== profileId && code)
      forgetParty(code);
    profileRef.current = profileId;
  }, [profileId, code]);

  const over = !!code && (state?.status === 'ended' || (!!error && !state));
  useEffect(() => {
    if (!over || !code) return;
    // The party page and the player say so themselves.
    if (pathname !== `/party/${code}` && !search.includes(`party=${code}`))
      toast(state?.endedReason ?? error ?? 'This party has ended.');
    setActiveParty(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, code]);

  // A title starts: the player opens once for everyone who isn't sitting it out.
  const pulled = useRef('');
  const round = state?.round ?? 0;
  const live = !!state && partyIsLive(state);
  const sittingOut = !!me?.sittingOut;
  useEffect(() => {
    if (!state || !live || sittingOut) return;
    const key = `${state.code}:${round}`;
    if (pulled.current === key) return;
    pulled.current = key;
    const to = partyWatchPath(state.media, state.code);
    if (!isPlayerFor(to, pathname, search)) navigate(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, sittingOut, round, state?.code]);

  // What you're looking at, for the others in the party.
  const place = /^\/watch\//.test(pathname) ? 'Getting ready to watch' : placeText(pathname);
  const lookingKey = looking ? `${looking.contentType}:${looking.mediaId}` : '';
  const connected = !!state && state.status !== 'ended';
  useEffect(() => {
    if (!conn || !connected) return;
    const t = window.setTimeout(() => {
      void conn
        .act({
          type: 'looking',
          place,
          media: looking
            ? { type: looking.contentType, id: looking.mediaId, title: looking.title }
            : null,
        })
        .catch(() => undefined);
    }, 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn, connected, place, lookingKey]);

  return null;
}

/** The player is already open on this party's title (a Request to Join carries on where it is). */
function isPlayerFor(to: string, pathname: string, search: string) {
  const [path, query] = to.split('?');
  return pathname === path && search.includes(query!);
}

/**
 * The party in the profile menu: Start a Party when you're not in one (a menu item, so starting one
 * is always a choice and never a stray click), or show and hide the party you're in. Null where
 * parties aren't offered (the web app).
 */
export function usePartyMenuItem() {
  const enabled = usePartyEnabled();
  const { state } = useActiveParty();
  const open = usePartyDock((s) => s.open);
  const [busy, setBusy] = useState(false);
  if (!enabled) return null;
  if (state && state.status !== 'ended')
    return {
      label: open ? 'Hide Your Party' : 'Show Your Party',
      members: state.members.length,
      busy: false,
      onSelect: () => setPartyDockOpen(!open),
    };
  return {
    label: 'Start a Party',
    members: 0,
    busy,
    onSelect: async () => {
      setBusy(true);
      try {
        await startParty();
      } catch (e) {
        fail(e);
      } finally {
        setBusy(false);
      }
    },
  };
}

/** Seconds left on "Up next in 15s", redrawn as it runs. */
export function useSecondsLeft(state: PartyState, conn: PartyConnection) {
  const c = state.countdown;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!c || c.pausedLeft !== null) return;
    const t = window.setInterval(() => tick((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [c]);
  if (!c) return 0;
  const ms = c.pausedLeft ?? c.endsAt - conn.serverNow();
  return Math.max(0, Math.ceil(ms / 1000));
}

/**
 * The party, in a column beside whatever you're browsing. There's no party page to go back to: who's
 * here, what's next and the chat stay in view on every page, title pages and settings included.
 */
export function PartyDock() {
  const { conn, state, me, isHost, messages, unread } = useActiveParty();
  const open = usePartyDock((s) => s.open);
  const inParty = !!conn && !!state && state.status !== 'ended';
  const shown = inParty && open;

  useEffect(() => {
    if (!shown) return;
    document.documentElement.setAttribute('data-party-dock', '');
    return () => document.documentElement.removeAttribute('data-party-dock');
  }, [shown]);

  // Comes back by itself when there's something you'd want to know.
  const counting = !!state?.countdown;
  const missing = !!state && filmOnWithoutYou(state, me);
  useEffect(() => {
    if (counting || missing) setPartyDockOpen(true);
  }, [counting, missing]);

  if (!shown) return null;
  const owner = state.members.find((m) => m.isHost);
  return (
    <aside
      aria-label="Party"
      className="party-dock fixed inset-y-0 right-0 z-[46] flex w-full max-w-[360px] flex-col border-l border-line bg-bg-2 shadow-2xl shadow-black/60 lg:shadow-none"
    >
      <DockBody
        conn={conn}
        state={state}
        me={me}
        isHost={isHost}
        messages={messages}
        unread={unread}
        name={isHost ? 'Your Party' : `${owner?.name ?? 'A friend'}'s Party`}
        ownerName={owner?.name ?? 'the owner'}
      />
      <Reactions code={state.code} />
      <ReadyCheckCard code={state.code} />
    </aside>
  );
}

function DockBody({
  conn,
  state,
  me,
  isHost,
  messages,
  unread,
  name,
  ownerName,
}: {
  conn: PartyConnection;
  state: PartyState;
  me: PartyMember | null;
  isHost: boolean;
  messages: PartyChatMessage[];
  unread: number;
  name: string;
  ownerName: string;
}) {
  const [tab, setTab] = useState<'queue' | 'chat'>('queue');
  // Open while you're alone in it, because inviting is the only thing to do.
  const [inviting, setInviting] = useState<boolean | null>(null);
  const showInvite = inviting ?? state.members.length === 1;

  useEffect(() => {
    conn.setChatOpen(tab === 'chat');
    return () => conn.setChatOpen(false);
  }, [conn, tab]);

  return (
    <>
      <div className="flex h-[72px] shrink-0 items-center gap-1 pl-5 pr-3">
        <h2 className="min-w-0 flex-1 truncate font-display text-lg font-semibold">{name}</h2>
        <button
          onClick={() => setInviting(!showInvite)}
          aria-label="Invite people"
          aria-pressed={showInvite}
          title="Invite people"
          className={cn(
            'grid h-8 w-8 place-items-center rounded-full transition-colors',
            showInvite ? 'bg-pink/15 text-pink' : 'text-muted hover:bg-white/8 hover:text-text',
          )}
        >
          <UserPlus className="h-4 w-4" />
        </button>
        {isHost && (
          <PartyMenu conn={conn} state={state} onLeave={() => void leaveParty(state.code)} />
        )}
        <button
          onClick={() => setPartyDockOpen(false)}
          aria-label="Hide the party"
          title="Hide the party"
          className="grid h-8 w-8 place-items-center rounded-full text-muted hover:bg-white/8 hover:text-text"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>

      {showInvite && <Invite code={state.code} />}
      <Roster conn={conn} state={state} isHost={isHost} />
      <Stage conn={conn} state={state} me={me} isHost={isHost} ownerName={ownerName} />

      <div className="mt-2 flex shrink-0 gap-5 border-b border-line px-5">
        <DockTab active={tab === 'queue'} onClick={() => setTab('queue')}>
          Queue
          {state.poll.length > 0 && <span className="text-muted">{state.poll.length}</span>}
        </DockTab>
        <DockTab active={tab === 'chat'} onClick={() => setTab('chat')}>
          Chat
          {unread > 0 && (
            <span className="grid h-4 min-w-4 place-items-center rounded-full bg-pink px-1 text-[10px] font-bold text-on-pink">
              {unread}
            </span>
          )}
        </DockTab>
      </div>
      <div className="min-h-0 flex-1">
        {tab === 'queue' ? (
          <PartyQueue conn={conn} state={state} isHost={isHost} meId={me?.id ?? null} />
        ) : (
          <Chat conn={conn} messages={messages} meId={me?.id ?? null} />
        )}
      </div>
      <EndOrLeave conn={conn} state={state} isHost={isHost} />
    </>
  );
}

/**
 * Always in view at the foot of the panel. Ending takes one click when you're on your own; with
 * others in the party it asks once, because it ends it for them too.
 */
function EndOrLeave({
  conn,
  state,
  isHost,
}: {
  conn: PartyConnection;
  state: PartyState;
  isHost: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const end = () => void conn.act({ type: 'end' }).catch(fail);
  const base =
    'flex h-10 flex-1 items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-colors';
  return (
    <div className="flex shrink-0 gap-2 border-t border-line p-3">
      {!isHost ? (
        <button
          onClick={() => void leaveParty(state.code)}
          className={cn(base, 'bg-white/8 hover:bg-white/12')}
        >
          <LogOut className="h-4 w-4" /> Leave Party
        </button>
      ) : asking ? (
        <>
          <button onClick={end} className={cn(base, 'bg-danger text-white hover:opacity-90')}>
            End for everyone
          </button>
          <button
            onClick={() => setAsking(false)}
            className={cn(base, 'bg-white/8 hover:bg-white/12')}
          >
            Keep it going
          </button>
        </>
      ) : (
        <button
          onClick={() => (state.members.length > 1 ? setAsking(true) : end())}
          className={cn(base, 'bg-white/8 text-danger hover:bg-white/12')}
        >
          <X className="h-4 w-4" /> End Party
        </button>
      )}
    </div>
  );
}

function DockTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 py-2.5 text-sm font-semibold transition-colors',
        active ? 'border-pink text-text' : 'border-transparent text-muted hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

/** Everyone in the party and what they're up to, always in view. */
function Roster({
  conn,
  state,
  isHost,
}: {
  conn: PartyConnection;
  state: PartyState;
  isHost: boolean;
}) {
  return (
    <ul aria-label="Who's here" className="scrollbar-thin max-h-44 shrink-0 overflow-y-auto px-3">
      {state.members.map((m) => {
        const you = m.id === state.you;
        const status = statusOf(m, state.readyCheck);
        const looking = !you && m.activity?.media && status === partyActivityText(m);
        return (
          <li key={m.id} className="flex items-center gap-3 rounded-xl px-2 py-1.5">
            <span className="relative shrink-0">
              <ProfileAvatar
                avatar={m.avatar}
                image={m.avatarUrl}
                size={36}
                rounded="full"
                className={cn(!m.connected && 'opacity-40')}
              />
              {m.connected && (
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success ring-2 ring-bg-2" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5 text-sm font-semibold">
                <span className="truncate">{you ? 'You' : m.name}</span>
                {m.isHost && (
                  <Crown className="h-3.5 w-3.5 shrink-0 text-pink" aria-label="Owner" />
                )}
              </span>
              {looking ? (
                <Link
                  to={detailsPath(m.activity!.media!)}
                  className="block truncate text-xs text-pink hover:underline"
                >
                  {status}
                </Link>
              ) : (
                <span className="block truncate text-xs text-muted">{status}</span>
              )}
            </span>
            {isHost && !you && <MemberMenu conn={conn} member={m} />}
          </li>
        );
      })}
    </ul>
  );
}

/** What's on, or what's about to be: the one thing in the party you might need to act on. */
function Stage({
  conn,
  state,
  me,
  isHost,
  ownerName,
}: {
  conn: PartyConnection;
  state: PartyState;
  me: PartyMember | null;
  isHost: boolean;
  ownerName: string;
}) {
  const navigate = useNavigate();
  const seconds = useSecondsLeft(state, conn);
  const next = state.poll[0];
  const start = () => next && void conn.act({ type: 'startNow', optionId: next.id }).catch(fail);

  if (partyIsLive(state)) {
    return (
      <StageCard label="Now playing" poster={state.media.posterUrl}>
        <StageText eyebrow="Now playing" title={partyMediaLabel(state.media)} />
        {filmOnWithoutYou(state, me) && (
          <Button size="sm" className="mt-2" onClick={() => jumpIn(conn, state, navigate)}>
            <Play className="h-4 w-4 fill-current" /> Jump in
          </Button>
        )}
      </StageCard>
    );
  }
  // A title picked in an older app, still waiting to be started.
  if (state.hasTitle && state.status === 'lobby') {
    return (
      <StageCard label="Next up" poster={state.media.posterUrl}>
        <StageText eyebrow="Next up" title={partyMediaLabel(state.media)} />
        {isHost && (
          <Button
            size="sm"
            className="mt-2"
            onClick={() => void conn.act({ type: 'start' }).catch(fail)}
          >
            <Play className="h-4 w-4 fill-current" /> Start Watch Together
          </Button>
        )}
      </StageCard>
    );
  }
  if (!next) return null;
  const c = state.countdown;
  if (c) {
    const paused = c.pausedLeft !== null;
    return (
      <StageCard label="Up next" status poster={next.media.posterUrl}>
        <StageText
          eyebrow={paused ? `Up next, paused at ${seconds}s` : `Up next in ${seconds}s`}
          title={partyMediaLabel(next.media)}
        />
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void conn.act({ type: 'countdown', paused: !paused }).catch(fail)}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            {paused ? 'Carry on' : 'Pause'}
          </Button>
          {isHost && (
            <Button size="sm" onClick={start}>
              <Play className="h-4 w-4 fill-current" /> Start now
            </Button>
          )}
        </div>
      </StageCard>
    );
  }
  const cant = state.members.some((m) => next.playable[m.id] === false);
  return (
    <StageCard label="Next up" poster={next.media.posterUrl}>
      <StageText eyebrow="Next up" title={partyMediaLabel(next.media)} />
      <p className={cn('mt-0.5 truncate text-xs', cant ? 'text-warning' : 'text-muted')}>
        {partyPlayableText(next, state.members)}
      </p>
      {isHost ? (
        <Button size="sm" className="mt-2" onClick={start}>
          <Play className="h-4 w-4 fill-current" /> Start now
        </Button>
      ) : (
        <p className="mt-1 truncate text-xs text-muted">Waiting for {ownerName} to start</p>
      )}
    </StageCard>
  );
}

function StageCard({
  label,
  status,
  poster,
  children,
}: {
  label: string;
  status?: boolean;
  poster: string | null;
  children: ReactNode;
}) {
  return (
    <section
      role={status ? 'status' : 'region'}
      aria-label={label}
      className="relative mx-4 mt-3 flex shrink-0 gap-3 overflow-hidden rounded-2xl bg-card p-3"
    >
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-125 object-cover opacity-25 blur-2xl"
        />
      )}
      {poster ? (
        <img src={poster} alt="" className="relative h-24 w-16 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="relative h-24 w-16 shrink-0 rounded-lg bg-elevated" />
      )}
      <div className="relative flex min-w-0 flex-1 flex-col items-start justify-center">
        {children}
      </div>
    </section>
  );
}

function StageText({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <>
      <p className="text-xs font-semibold text-pink">{eyebrow}</p>
      <p className="w-full truncate font-semibold">{title}</p>
    </>
  );
}

function Invite({ code }: { code: string }) {
  const { linkUrl } = useLinks();
  const [sent, setSent] = useState<string[]>([]);
  const can = useCanSocialise();
  const friends = useFriends(can).data?.friends ?? [];
  const invite = `${linkUrl}/party/${code}`;

  async function copy() {
    if (await copyText(invite, 'invite link')) toast.success('Invite link copied');
  }

  async function send(id: string) {
    try {
      await api(`/api/party/${code}/invite`, { body: { to: id } });
      setSent((s) => [...s, id]);
    } catch (e) {
      fail(e);
    }
  }

  return (
    <section aria-label="Invite people" className="mx-4 mb-3 shrink-0 rounded-2xl bg-card p-3">
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate text-xs text-muted">{invite}</code>
        <Button size="sm" variant="secondary" onClick={copy} aria-label="Copy invite link">
          <Copy className="h-4 w-4" /> Copy
        </Button>
      </div>
      {friends.length > 0 && (
        <ul className="scrollbar-thin mt-2 max-h-32 space-y-0.5 overflow-y-auto">
          {friends.map((f) => (
            <li key={f.id} className="flex items-center gap-2.5 py-1">
              <ProfileAvatar avatar={f.avatar} image={f.avatarUrl} size={28} rounded="full" />
              <span className="min-w-0 flex-1 truncate text-sm">{f.name}</span>
              <button
                disabled={sent.includes(f.id)}
                onClick={() => void send(f.id)}
                aria-label={`Invite ${f.name}`}
                className="flex h-7 items-center gap-1 rounded-full bg-white/8 px-2.5 text-xs font-semibold hover:bg-white/12 disabled:text-muted"
              >
                {sent.includes(f.id) ? <Check className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                {sent.includes(f.id) ? 'Invited' : 'Invite'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** On a title page in a party: Add to queue, plus Start now for the owner. */
export function PartyTitleActions({ title }: { title: PartyTitle | null | undefined }) {
  const { conn, state, isHost } = useActiveParty();
  const [busy, setBusy] = useState(false);
  if (!conn || !state || state.status === 'ended' || !title) return null;
  const queued = state.poll.find(
    (o) =>
      o.media.id === title.id &&
      (o.media.season ?? null) === (title.season ?? null) &&
      (o.media.episode ?? null) === (title.episode ?? null),
  );
  const run = async (startNow: boolean) => {
    setBusy(true);
    try {
      await conn.nominate(title, startNow);
      if (!startNow) toast.success('Added to the queue');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const cant = queued && state.members.some((m) => queued.playable[m.id] === false);
  return (
    <>
      {queued ? (
        <span className="inline-flex h-13 items-center gap-2 rounded-2xl border border-pink px-5 text-[15px] font-semibold text-pink">
          <Check className="h-4 w-4" /> In the queue
        </span>
      ) : (
        <Button
          variant="secondary"
          className="h-13 rounded-2xl"
          disabled={busy}
          onClick={() => void run(false)}
        >
          <Plus className="h-5 w-5" /> Add to queue
        </Button>
      )}
      {isHost && (
        <Button
          variant="secondary"
          className="h-13 rounded-2xl"
          disabled={busy}
          onClick={() => void run(true)}
        >
          <Play className="h-5 w-5" /> Start now
        </Button>
      )}
      {/* Its own line, whatever else is in the row: the buttons stay on one line. */}
      {queued && (
        <p className={cn('order-last w-full text-sm', cant ? 'text-warning' : 'text-muted')}>
          {partyPlayableText(queued, state.members)}
        </p>
      )}
    </>
  );
}
